import { isAllowedInLiveResponse, MowerEnvironmentRequest, SuccessResponse } from "models/apiModels";
import { AmcError } from "models/errors/errorModels";
import { MowerEnvironmentSession } from "models/remoteDebugSessionModel";
import { resetTaskStatus, setTaskStartedById, setTaskSuccessfulById, setTaskErrorById } from "store/mowerEnvironmentState/mowerEnvironmentState";
import { toolsApi } from "./tools-v1";
import { mowerLogInWithRetry, remoteDebugSessionStartedWithRetry } from "./helpers";

const mowerEnvironmentApi = toolsApi.injectEndpoints({
  endpoints: (builder) => ({
    mowerEnvironment: builder.mutation<SuccessResponse, MowerEnvironmentRequest>({
      async queryFn(req, _api, _extraOptions, baseQuery) {
        const serialNumber = Number(req.mowerId.split('-')[0]);
        const minutes = 1000 * 60 * 7;
        const oldEnv: MowerEnvironmentSession = 'OLD_ENV';
        let currentTask = '';

        _api.dispatch(resetTaskStatus());

        currentTask = 'isAllowed'
        _api.dispatch(setTaskStartedById(currentTask));
        if (req.newEnvironment === 'live') {
          const allowedInLiveResult = await baseQuery(
            `/tools/v1/mowers/${serialNumber}-999999999/allowed-in-live`,
          );

          if (allowedInLiveResult.error) {
            _api.dispatch(setTaskErrorById(currentTask));
            return { error: allowedInLiveResult.error };
          } 

          if (
            isAllowedInLiveResponse(allowedInLiveResult.data) &&
            allowedInLiveResult.data.isAllowed === false
          ) {
            const customRtkQueryError: AmcError = {
              status: 400,
              data: {
                title: 'Bad request',
                detail: 'Mowers with keyId 0 (i.e. WinMowers) are not allowed in Live',
                statusCode: 400,
                id: 'custom-id',
              },
            };
            return { error: customRtkQueryError };
          }
        }
        _api.dispatch(setTaskSuccessfulById(currentTask));

        currentTask = 'ownership';
        _api.dispatch(setTaskStartedById(currentTask));

        const proxyincloudResult = await baseQuery({
          url: '/tools/v1/mowers/proxyincloud/ownership',
          method: 'POST',
          body: { serialNumber },
        });

        if (proxyincloudResult.error) {
          _api.dispatch(setTaskErrorById(currentTask));
          return { error: proxyincloudResult.error };
        }
        _api.dispatch(setTaskSuccessfulById(currentTask));

        currentTask = 'remoteDebugSession';
        _api.dispatch(setTaskStartedById(currentTask));
        const remoteDebugResult = await baseQuery({
          url: `/tools/v1/mowers/${serialNumber}-999999999/remoteDebugSession`,
          method: 'POST',
          body: {
            requestedBy: req.requestedBy,
            endingTimestamp: Date.now() + minutes,
            mowerEnvironmentSession: oldEnv,
          },
        });

        if (remoteDebugResult.error) {
          _api.dispatch(setTaskErrorById(currentTask));
          return { error: remoteDebugResult.error };
        }
        _api.dispatch(setTaskSuccessfulById(currentTask));

        currentTask = 'remoteDebugSessionStart';
        _api.dispatch(setTaskStartedById(currentTask));
        const remoteDebugSessionStarted = await remoteDebugSessionStartedWithRetry(
          serialNumber,
          baseQuery,
        );

        if (!remoteDebugSessionStarted) {
          const customRtkQueryError: AmcError = {
            status: 400,
            data: {
              title: 'Bad request',
              detail: 'The backend could not reach the mower via AMC Backend.',
              statusCode: 400,
              id: 'custom-id',
            },
          };
          _api.dispatch(setTaskErrorById(currentTask));
          return { error: customRtkQueryError };
        }
        _api.dispatch(setTaskSuccessfulById(currentTask));

        currentTask = 'mowerLogIn';
        _api.dispatch(setTaskStartedById(currentTask));
        const isMowerLogInCompleted = await mowerLogInWithRetry(serialNumber, baseQuery);

        if (!isMowerLogInCompleted) {
          const customRtkQueryError: AmcError = {
            status: 418,
            data: {
              title: 'Unsucessful login.',
              detail: 'Error when trying to reach mower.',
              statusCode: 418,
              id: 'custom-id',
            },
          };
          _api.dispatch(setTaskErrorById(currentTask));
          return { error: customRtkQueryError };
        }
        _api.dispatch(setTaskSuccessfulById(currentTask));

        currentTask = 'changeMowerHost';
        _api.dispatch(setTaskStartedById(currentTask));
        const amcBackendHostResult = await baseQuery({
          url: `/tools/v1/mowers/${serialNumber}-999999999/proxyincloud/amc-backend-host`,
          method: 'PUT',
          body: { env: req.newEnvironment },
        });

        if (amcBackendHostResult.error) {
          _api.dispatch(setTaskErrorById(currentTask));
          return { error: amcBackendHostResult.error };
        }
        _api.dispatch(setTaskSuccessfulById(currentTask));

        currentTask = 'reconnectMower';
        _api.dispatch(setTaskStartedById(currentTask));
        const amcBackendReConnectResult = await baseQuery({
          url: `/tools/v1/mowers/${serialNumber}-999999999/proxyincloud/amc-backend-reconnect`,
        });

        if (amcBackendReConnectResult.error) {
          _api.dispatch(setTaskErrorById(currentTask));
          return { error: amcBackendReConnectResult.error };
        }
        _api.dispatch(setTaskSuccessfulById(currentTask));

        return { data: { response: 'Request succeeded.' } };
      },
    }),

    mowerEnvironmentCleanUp: builder.mutation<SuccessResponse, MowerEnvironmentRequest>({
      async queryFn(req, _api, _extraOptions, baseQuery) {
        const serialNumber = Number(req.mowerId.split('-')[0]);
        const minutes = 1000 * 60 * 7;
        const newEnv: MowerEnvironmentSession = 'NEW_ENV';

        const remoteDebugNewEnvResult = await baseQuery({
          url: `/tools/v1/mowers/${serialNumber}-999999999/remoteDebugSession`,
          method: 'POST',
          body: {
            requestedBy: req.requestedBy,
            endingTimestamp: Date.now() + minutes,
            mowerEnvironmentSession: newEnv,
          },
        });
        if (remoteDebugNewEnvResult.error) return { error: remoteDebugNewEnvResult.error };

        return { data: { response: 'Request succeeded.' } };
      },
    }),
  }),

  overrideExisting: false,
})

export const { useMowerEnvironmentMutation, useMowerEnvironmentCleanUpMutation } = mowerEnvironmentApi